import { ReactNode, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  selectSelectedTake3D,
  selectTakes3D,
  setSelectedTakeSequence,
} from "../../../app/slices/TrainingSlice";
import { getTakeSetupPlan } from "../../../helpers/TakeSetupPlan";
import { getTriggerChainNodeIds, ON_TAKE_END } from "../../../helpers/TakeTriggerChain";
import { getTake3DSequence } from "../../../models/Take3D";
import usePreviousTakeExit from "./usePreviousTakeExit";
import {
  Alongside,
  cellClassName,
  clickableClassName,
  Fade,
  FADE_SECONDS,
  getTakeFlowWidth,
  HANDLE_OUTSET,
  pressProps,
} from "./TakeSetupFlow";

/**
 * The selected take's On Take End: its nodes play in the next take's black
 * screen (or setup, without a transition), at the same time as that take's
 * setup. Shown from this side of the handover. `handle` is the trigger's output.
 */
export default function TakeEndHandover(props: { handle: ReactNode }) {
  const dispatch = useDispatch();
  const takes = useSelector(selectTakes3D);
  const take = useSelector(selectSelectedTake3D);
  const index = take ? takes.findIndex((candidate) => candidate.id === take.id) : -1;
  const nextTake = index >= 0 ? takes[index + 1] : undefined;

  const plan = useMemo(
    () => (take && nextTake ? getTakeSetupPlan(take, nextTake) : undefined),
    [take, nextTake],
  );
  const ownNodeCount = useMemo(
    () => getTriggerChainNodeIds(getTake3DSequence(take), ON_TAKE_END).length,
    [take],
  );

  // As wide as this take's On Take Start, which is wider when the previous take's exit runs beside its setup.
  const width = getTakeFlowWidth(Boolean(usePreviousTakeExit()));

  const handle = (
    <div className="absolute top-1/2 visible" style={{ right: -HANDLE_OUTSET }}>
      {props.handle}
    </div>
  );

  if (!take || !nextTake || !plan) {
    // The compiler only plays On Take End when the next take starts.
    return (
      <div
        className="relative flex flex-col text-[12px] leading-[16px]"
        style={{ width }}
        data-testid="take-end-last-take"
      >
        <div
          className={classNames(
            "rounded-[4px] px-[10px] py-[8px]",
            ownNodeCount > 0
              ? "bg-creator-error-washed text-creator-error"
              : "bg-creator-background text-creator-text-soft",
          )}
        >
          Never plays in the last take
        </div>
        {handle}
      </div>
    );
  }

  const nextName = nextTake.name;
  const open = () => dispatch(setSelectedTakeSequence(nextTake.id));

  return (
    <div
      className="flex flex-col gap-[3px] text-[12px] leading-[16px]"
      style={{ width }}
      data-testid="take-end-handover"
    >
      {plan.transition && <Fade label="Fade in" seconds={FADE_SECONDS} />}
      <div className={cellClassName} data-testid="take-end-next-setup">
        {plan.transition ? (
          <div className="-mx-[10px] -mt-[8px] flex items-center rounded-t-[4px] bg-black px-[10px] py-[5px] font-medium text-white">
            Black screen
            <span className="ml-auto font-normal tabular-nums opacity-70">
              {plan.transition.duration} s
            </span>
          </div>
        ) : (
          <div className="font-medium text-creator-text">Setup, in view</div>
        )}
        <div className="flex gap-[10px]">
          <div className="flex min-w-0 flex-1 flex-col">
            <div
              {...pressProps(open)}
              title={`Open ${nextName}`}
              className={classNames(
                "-mx-[6px] flex min-h-[20px] items-center gap-[2px] self-start rounded-[4px] px-[6px] font-medium text-creator-text",
                clickableClassName,
              )}
              data-testid="take-end-open-next"
            >
              <span className="truncate">{nextName} setup</span>
              <ChevronRightIcon className="flex-shrink-0 text-creator-text-soft" sx={{ fontSize: 16 }} />
            </div>
          </div>
          <Alongside testId="take-end-alongside">
            <div className="relative flex flex-col gap-[2px] rounded-[4px] bg-creator-primary-washed px-[10px] py-[8px]">
              <span className="font-medium text-creator-text">These nodes</span>
              <span className="text-creator-text-soft">Start with the setup</span>
              {handle}
            </div>
          </Alongside>
        </div>
      </div>
      {plan.transition && <Fade label="Fade out" seconds={FADE_SECONDS} />}
    </div>
  );
}
